import { CSSProperties, FormEvent, ReactElement, ReactNode, useEffect } from 'react'
import { useForm, FormProvider, UseFormReturn, FieldValues, DefaultValues } from 'react-hook-form'
import { useLatest } from 'react-use'
import { AnyObjectSchema } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppStore } from '@stores'

interface Props<T extends FieldValues> {
  id?: string

  /** Passed to `useForm` — use `onChange` when you want resolver validation on every edit. @default 'onSubmit' */
  mode?: 'onSubmit' | 'onBlur' | 'onChange' | 'onTouched' | 'all'

  onSubmit: (
    values: T,
    event: FormEvent<HTMLFormElement>,
    isDirty: boolean,
    touchedFields: Record<string, unknown>,
  ) => void | Promise<void | unknown>

  children?: ReactNode
  validationSchema?: AnyObjectSchema
  validationContext?: unknown
  defaultValues: T
  'data-cy'?: string
  style?: CSSProperties
  resetIsDirtyAfterSubmit?: boolean
  reinitialize?: true | ((currentValues: T, defaultValues: T) => T)
  submitOnChange?: boolean
}

const Form2 = function Form2<T extends FieldValues>({
  onSubmit,
  children,
  defaultValues,
  validationSchema,
  id = '',
  validationContext,
  'data-cy': dataCy,
  style,
  resetIsDirtyAfterSubmit = false,
  reinitialize: reinitializeFunction,
  submitOnChange = false,
  mode = 'onSubmit',
}: Props<T>): ReactElement {
  const reinitializeRef = useLatest(reinitializeFunction)
  const disableFormValidation = useAppStore(state => state.disableFormValidation)

  const methods: UseFormReturn<T> = useForm<T>({
    mode,
    reValidateMode: 'onChange',
    defaultValues: defaultValues as DefaultValues<T> | undefined,
    context: validationContext,
    ...(validationSchema && !disableFormValidation
      ? { resolver: yupResolver(validationSchema) }
      : {}),
  })

  useEffect(() => {
    if (reinitializeRef.current) {
      if (reinitializeRef.current === true) {
        methods.reset(defaultValues)
      } else {
        const currentValues = methods.getValues()
        const newValues = reinitializeRef.current?.(currentValues, defaultValues)
        methods.reset(newValues)
      }
    }
  }, [defaultValues, reinitializeRef, methods])

  // Handle submit on change
  useEffect(() => {
    if (!submitOnChange) return

    const subscription = methods.watch((values, { name, type }) => {
      // Only submit if the field was actually changed by user interaction
      // and not during initialization or reset
      if (type === 'change' && name) {
        const isDirty = methods.formState.isDirty
        const touchedFields = methods.formState.touchedFields

        // Create a synthetic event for the onSubmit handler
        const syntheticEvent = {
          stopPropagation: () => {
            // No-op for synthetic event
          },
          preventDefault: () => {
            // No-op for synthetic event
          },
        } as FormEvent<HTMLFormElement>

        methods.handleSubmit(async data => {
          onSubmit(data, syntheticEvent, isDirty, touchedFields)
          if (resetIsDirtyAfterSubmit) {
            methods.reset(data, { keepValues: true })
          }
        })()
      }
    })

    return () => subscription.unsubscribe()
  }, [submitOnChange, methods, onSubmit, resetIsDirtyAfterSubmit])

  return (
    <FormProvider {...methods}>
      <form
        id={id}
        data-cy={dataCy || id}
        onSubmit={event => {
          event.stopPropagation()

          const isDirty = methods.formState.isDirty
          const touchedFields = methods.formState.touchedFields

          methods.handleSubmit(async data => {
            await onSubmit(data, event, isDirty, touchedFields)
            if (resetIsDirtyAfterSubmit) {
              methods.reset(data, { keepValues: true })
            }
          })(event)
        }}
        style={style}
      >
        {children}
      </form>
    </FormProvider>
  )
}

export default Form2
